import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { transactionsAPI, audienceAPI, analyticsAPI, assignmentsAPI, orgCalendarAPI } from '../../api';
import { summariseInvites } from '../../util/calendarInvites';
import { orgAudienceAPI } from '../../api/clients/orgAudienceAPI';
import { fetchOrganizationById } from '../../ducks/organizations.duck';
import { getSendbirdInstance, initializeSendbird } from '../../util/sendbird';
import { sendbirdAPI } from '../../api/clients/sendbirdAPI';
import { selectSendbirdInitialized } from '../../ducks/sendbird.duck';
import config from '../../config';
import { PAGE_TYPE_QUERY, isPageType } from '../../util/listingType';
import { ctaAPI } from '../../api/clients/ctaAPI';

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/InsightsPage/SET_INITIAL_STATE';
export const FETCH_INSIGHTS_REQUEST = 'app/InsightsPage/FETCH_INSIGHTS_REQUEST';
export const FETCH_INSIGHTS_SUCCESS = 'app/InsightsPage/FETCH_INSIGHTS_SUCCESS';
export const FETCH_INSIGHTS_ERROR = 'app/InsightsPage/FETCH_INSIGHTS_ERROR';
export const SET_DATE_RANGE = 'app/InsightsPage/SET_DATE_RANGE';
export const SET_CTA_CLICKS = 'app/InsightsPage/SET_CTA_CLICKS';

// ================ Helpers ================ //

/**
 * Compute the start date for a given range in days from today.
 */
const getStartDateForRange = (rangeDays) => {
  const now = new Date();
  return new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000);
};

/**
 * Format a date to a short label for chart display.
 * For 7 days -> day name (Mon, Tue, ...)
 * For 30 days -> Week N
 * For 90/190 days -> month abbreviation (Jan, Feb, ...)
 */
const formatDateLabel = (date, rangeDays) => {
  if (rangeDays <= 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (rangeDays <= 30) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  // Include year when the range spans more than 12 months so "Jan"
  // labels from different years don't collide on the chart.
  if (rangeDays > 365) {
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short' });
};

/**
 * Group transactions by date bucket and compute daily revenue.
 * Returns an array of { date: 'label', revenue: amountInDollars }
 */
const computeDailyRevenue = (transactions, rangeDays) => {
  const startDate = getStartDateForRange(rangeDays);
  const now = new Date();

  // Filter to transactions within range
  const filtered = transactions.filter(t => {
    const createdAt = new Date(t.attributes.createdAt);
    return createdAt >= startDate && createdAt <= now;
  });

  if (rangeDays <= 7) {
    // Daily buckets
    const buckets = {};
    for (let i = 0; i < rangeDays; i++) {
      const d = new Date(now.getTime() - (rangeDays - 1 - i) * 24 * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = { date: formatDateLabel(d, rangeDays), revenue: 0 };
    }
    filtered.forEach(t => {
      const key = new Date(t.attributes.createdAt).toISOString().slice(0, 10);
      if (buckets[key]) {
        const payout = t.attributes.payoutTotal;
        buckets[key].revenue += payout ? payout.amount : 0;
      }
    });
    return Object.values(buckets);
  }

  if (rangeDays <= 30) {
    // Weekly buckets
    const weeks = [];
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(now.getTime() - (4 - i) * 7 * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      weeks.push({
        start: weekStart,
        end: weekEnd,
        date: `Week ${i + 1}`,
        revenue: 0,
      });
    }
    filtered.forEach(t => {
      const createdAt = new Date(t.attributes.createdAt);
      const payout = t.attributes.payoutTotal;
      const amount = payout ? payout.amount : 0;
      for (const week of weeks) {
        if (createdAt >= week.start && createdAt < week.end) {
          week.revenue += amount;
          break;
        }
      }
    });
    return weeks.map(w => ({ date: w.date, revenue: w.revenue }));
  }

  // Monthly buckets for 90/190 days
  const monthMap = {};
  const monthOrder = [];
  filtered.forEach(t => {
    const createdAt = new Date(t.attributes.createdAt);
    const key = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
    // Route through formatDateLabel so ranges over 12 months add the year and
    // "Jan" from different years don't collide on the x-axis (All-time view).
    const label = formatDateLabel(createdAt, rangeDays);
    if (!monthMap[key]) {
      monthMap[key] = { date: label, revenue: 0 };
      monthOrder.push(key);
    }
    const payout = t.attributes.payoutTotal;
    monthMap[key].revenue += payout ? payout.amount : 0;
  });
  monthOrder.sort();
  return monthOrder.map(k => ({ date: monthMap[k].date, revenue: monthMap[k].revenue }));
};

/**
 * Compute percentage change between current and previous period.
 */
const computeChange = (currentValue, previousValue) => {
  if (previousValue === 0) return currentValue > 0 ? 100 : 0;
  return Math.round(((currentValue - previousValue) / previousValue) * 100);
};

/**
 * Split transactions into current and previous period based on dateRange.
 */
const splitPeriods = (transactions, rangeDays) => {
  const now = new Date();
  const currentStart = getStartDateForRange(rangeDays);
  const previousStart = new Date(currentStart.getTime() - rangeDays * 24 * 60 * 60 * 1000);

  const current = transactions.filter(t => {
    const d = new Date(t.attributes.createdAt);
    return d >= currentStart && d <= now;
  });
  const previous = transactions.filter(t => {
    const d = new Date(t.attributes.createdAt);
    return d >= previousStart && d < currentStart;
  });

  return { current, previous };
};

/**
 * Sum payout totals from transactions (returns amount in cents).
 */
const sumPayoutCents = (transactions) => {
  return transactions
    .filter(t => t.attributes.payoutTotal)
    .reduce((sum, t) => sum + t.attributes.payoutTotal.amount, 0);
};

/**
 * Group transactions by listing and compute per-listing revenue + count.
 */
const groupByListing = (transactions) => {
  const map = {};
  transactions.forEach(t => {
    const listing = t.listing;
    if (!listing) return;
    const id = listing.id?.uuid || listing.id;
    if (!map[id]) {
      map[id] = {
        id,
        name: listing.attributes?.title || 'Unknown',
        revenue: 0,
        views: 0,
        transactions: 0,
        conversion: 0,
      };
    }
    const payout = t.attributes.payoutTotal;
    map[id].revenue += payout ? payout.amount : 0; // Keep in cents
    map[id].transactions += 1;
  });

  return Object.values(map)
    .sort((a, b) => b.revenue - a.revenue)
    .map(item => ({
      ...item,
      revenue: Math.round(item.revenue),
    }));
};

/**
 * Categorize transactions into revenue sources based on listing type / transaction process.
 */
const computeRevenueSources = (currentTransactions, previousTransactions) => {
  const categorize = (txns) => {
    const categories = {
      Memberships: 0,
      Tips: 0,
      Other: 0,
    };

    txns.forEach(t => {
      const payout = t.attributes.payoutTotal;
      const amount = payout ? payout.amount : 0; // Keep in cents
      const processName = t.attributes.processName || '';
      const listingType = t.listing?.attributes?.publicData?.type || '';

      if (
        processName.includes('subscription') ||
        processName.includes('membership') ||
        listingType === 'live-room'
      ) {
        categories.Memberships += amount;
      } else if (processName.includes('donation') || processName.includes('tip')) {
        categories.Tips += amount;
      } else {
        categories.Other += amount;
      }
    });
    return categories;
  };

  const currentCats = categorize(currentTransactions);
  const previousCats = categorize(previousTransactions);
  const total = Object.values(currentCats).reduce((s, v) => s + v, 0);

  return Object.entries(currentCats)
    .filter(([, value]) => value > 0)
    .map(([label, value]) => ({
      label,
      value: Math.round(value),
      percent: total > 0 ? Math.round((value / total) * 100) : 0,
      change: computeChange(value, previousCats[label] || 0),
    }))
    .sort((a, b) => b.value - a.value);
};

/**
 * Build daily audience chart data from lists.
 * Since we only get current totals from the API, we generate an approximate growth curve.
 */


/**
 * Compute engagement chart data from transactions over time.
 */
const computeEngagementDaily = (transactions, rangeDays) => {
  const startDate = getStartDateForRange(rangeDays);
  const now = new Date();

  const filtered = transactions.filter(t => {
    const d = new Date(t.attributes.createdAt);
    return d >= startDate && d <= now;
  });

  if (rangeDays <= 7) {
    const buckets = {};
    for (let i = 0; i < rangeDays; i++) {
      const d = new Date(now.getTime() - (rangeDays - 1 - i) * 24 * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = { date: formatDateLabel(d, rangeDays), views: 0 };
    }
    filtered.forEach(t => {
      const key = new Date(t.attributes.createdAt).toISOString().slice(0, 10);
      if (buckets[key]) buckets[key].views += 1;
    });
    return Object.values(buckets);
  }

  // Monthly buckets
  const monthMap = {};
  const monthOrder = [];
  filtered.forEach(t => {
    const d = new Date(t.attributes.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    // See computeDailyRevenue — year-qualify labels for >12-month ranges.
    const label = formatDateLabel(d, rangeDays);
    if (!monthMap[key]) {
      monthMap[key] = { date: label, views: 0 };
      monthOrder.push(key);
    }
    monthMap[key].views += 1;
  });
  monthOrder.sort();
  return monthOrder.map(k => monthMap[k]);
};

// ================ Reducer ================ //

const initialState = {
  // Tracks which org's data is loaded (undefined for the creator variant).
  // Used by SET_INITIAL_STATE to full-reset on an org switch.
  orgId: null,
  // Call-to-action clicks from the /go/ log: { available, total, byAction, bySource, items }
  ctaClicks: null,
  insightsLoading: false,
  insightsError: null,
  // Flipped to true after the first successful load. Tab components
  // read this to decide whether to render skeletons (first load) or
  // keep the previous numbers on screen (refetch / tab nav).
  insightsLoaded: false,
  dateRange: 30,

  // Earning tab
  totalRevenue: 0,
  revenueChange: 0,
  paidSubscribers: 0,
  subscribersChange: 0,
  totalViews: 0,
  viewsChange: 0,
  conversionRate: 0,
  conversionChange: 0,
  revenueSources: [],
  revenueDaily: [],
  performanceByListing: [],

  // Audience tab
  totalAudience: 0,
  audienceChange: 0,
  newSubscribers: 0,
  newSubscribersChange: 0,
  growthRate: 0,
  churnRate: 0,
  audienceDaily: [],
  topSegments: [],
  audienceLists: [],

  // Engagement tab
  totalTransactions: 0,
  transactionsChange: 0,
  roomParticipations: 0,
  participationsChange: 0,
  emailOpenRate: 0,
  openRateChange: 0,
  pollResponsesCount: 0,
  pollResponsesChange: 0,
  // Manual calendar events: how many people were invited and how they answered
  calendarInvites: { invited: 0, yes: 0, no: 0, pending: 0, events: 0 },
  totalPolls: 0,
  topPolls: [],
  engagementDaily: [],
  topContent: [],
  contentPerformance: [],

  // Provenance split — populated by mergeRentedIntoInsights on the
  // org variant whenever there are active assignments granting the
  // relevant consent. Tabs read this when the ProvenanceFilter chip
  // is flipped from "All" to "Owned" / "Rented".
  provenance: {
    totalRevenue: { owned: 0, rented: 0 },
    paidSubscribers: { owned: 0, rented: 0 },
    totalAudience: { owned: 0, rented: 0 },
    totalTransactions: { owned: 0, rented: 0 },
    roomParticipations: { owned: 0, rented: 0 },
    pollResponsesCount: { owned: 0, rented: 0 },
  },
  rentedAssignments: [],

  // Raw data for re-processing on date range change
  _rawTransactions: [],
  _rawListings: [],
  _rawAudienceLists: [],
  _rawEmails: [],
  _rawPollData: null,
  _rawPageViewData: null,
  _rawRentedBundle: null,
  _isOrg: false,
};

export default function insightsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      // Dispatched at the start of loadData, which re-runs on every tab /
      // date-range navigation. On an ORG SWITCH (payload orgId differs from
      // the loaded one, same mounted component) full-reset so the previous
      // org's numbers can't linger under the new org during its fetch —
      // keeping only the user's dateRange selection. Otherwise (tab /
      // date-range nav, or the creator variant where orgId is always
      // undefined) KEEP the loaded numbers, the rented overlay AND the raw
      // cache on screen — FETCH_INSIGHTS_SUCCESS overwrites them when the
      // load completes. Returning initialState unconditionally (as it used
      // to) flashed every metric card to 0 on each tab switch.
      if ((payload || null) !== (state.orgId || null)) {
        return { ...initialState, orgId: payload || null, dateRange: state.dateRange };
      }
      return { ...state, insightsError: null };
    case FETCH_INSIGHTS_REQUEST:
      return { ...state, insightsLoading: true, insightsError: null };
    case FETCH_INSIGHTS_SUCCESS:
      return { ...state, insightsLoading: false, insightsLoaded: true, ...payload };
    case FETCH_INSIGHTS_ERROR:
      return { ...state, insightsLoading: false, insightsError: payload };
    case SET_DATE_RANGE:
      return { ...state, dateRange: payload };
    case SET_CTA_CLICKS:
      return { ...state, ctaClicks: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = orgId => ({ type: SET_INITIAL_STATE, payload: orgId });
export const fetchInsightsRequest = () => ({ type: FETCH_INSIGHTS_REQUEST });
export const fetchInsightsSuccess = data => ({ type: FETCH_INSIGHTS_SUCCESS, payload: data });
export const fetchInsightsError = error => ({ type: FETCH_INSIGHTS_ERROR, payload: error });
export const setDateRangeAction = range => ({ type: SET_DATE_RANGE, payload: range });

// ================ Selectors ================ //

// Provenance split — populated on the org variant when there are
// active assignments granting viewInsights / temporaryAudienceAccess.
// Always defined (zeros when no rented data), so tab components can
// read it without guarding.
export const selectProvenance = state =>
  state.InsightsPage.provenance || { totalRevenue: { owned: 0, rented: 0 } };
export const selectRentedAssignments = state =>
  state.InsightsPage.rentedAssignments || [];

export const selectInsightsLoading = state => state.InsightsPage.insightsLoading;
export const selectInsightsLoaded = state => state.InsightsPage.insightsLoaded;
export const selectInsightsError = state => state.InsightsPage.insightsError;
export const selectDateRange = state => state.InsightsPage.dateRange;

// Earning selectors
export const selectTotalRevenue = state => state.InsightsPage.totalRevenue;
export const selectRevenueChange = state => state.InsightsPage.revenueChange;
export const selectPaidSubscribers = state => state.InsightsPage.paidSubscribers;
export const selectSubscribersChange = state => state.InsightsPage.subscribersChange;
export const selectTotalViews = state => state.InsightsPage.totalViews;
export const selectViewsChange = state => state.InsightsPage.viewsChange;
export const selectConversionRate = state => state.InsightsPage.conversionRate;
export const selectConversionChange = state => state.InsightsPage.conversionChange;
export const selectRevenueSources = state => state.InsightsPage.revenueSources;
export const selectRevenueDaily = state => state.InsightsPage.revenueDaily;
export const selectPerformanceByListing = state => state.InsightsPage.performanceByListing;

// Audience selectors
export const selectTotalAudience = state => state.InsightsPage.totalAudience;
export const selectAudienceChange = state => state.InsightsPage.audienceChange;
export const selectNewSubscribers = state => state.InsightsPage.newSubscribers;
export const selectNewSubscribersChange = state => state.InsightsPage.newSubscribersChange;
export const selectGrowthRate = state => state.InsightsPage.growthRate;
export const selectChurnRate = state => state.InsightsPage.churnRate;
export const selectAudienceDaily = state => state.InsightsPage.audienceDaily;
export const selectTopSegments = state => state.InsightsPage.topSegments;
export const selectAudienceLists = state => state.InsightsPage.audienceLists;

// Engagement selectors
export const selectTotalTransactions = state => state.InsightsPage.totalTransactions;
export const selectTransactionsChange = state => state.InsightsPage.transactionsChange;
export const selectRoomParticipations = state => state.InsightsPage.roomParticipations;
export const selectParticipationsChange = state => state.InsightsPage.participationsChange;
export const selectEmailOpenRate = state => state.InsightsPage.emailOpenRate;
export const selectOpenRateChange = state => state.InsightsPage.openRateChange;
export const selectEngagementDaily = state => state.InsightsPage.engagementDaily;
export const selectCtaClicks = state => state.InsightsPage.ctaClicks;
export const selectPollResponsesCount = state => state.InsightsPage.pollResponsesCount;
export const selectPollResponsesChange = state => state.InsightsPage.pollResponsesChange;
export const selectCalendarInvites = state => state.InsightsPage.calendarInvites;
export const selectTotalPolls = state => state.InsightsPage.totalPolls;
export const selectTopPolls = state => state.InsightsPage.topPolls;
export const selectTopContent = state => state.InsightsPage.topContent;
export const selectContentPerformance = state => state.InsightsPage.contentPerformance;

// ================ Processing ================ //

/**
 * Process all fetched data into the insights state shape.
 * This is called both on initial load and on date range changes.
 */
const processInsightsData = (transactions, listings, audienceLists, emails, rangeDays, pollData = null, isOrg = false, pageViewData = null) => {
  // ---- Earning data ----
  const { current, previous } = splitPeriods(transactions, rangeDays);
  const currentRevenueCents = sumPayoutCents(current);
  const previousRevenueCents = sumPayoutCents(previous);
  const totalRevenue = currentRevenueCents; // Keep in cents for formatMoney
  const revenueChange = computeChange(currentRevenueCents, previousRevenueCents);

  // Subscriber counts from listing metadata
  // Free joins are written to the same list with type 'free'. Paid joins carry
  // type 'paid' since 26 Sep 2026; before that the payment path wrote no type,
  // so an entry without one is a paid membership (each matches a payout
  // transaction). Only explicit free entries are excluded.
  const allSubscriptions = listings
    .filter(l => l.attributes?.metadata?.subscriptions)
    .flatMap(l => l.attributes.metadata.subscriptions);
  const paidSubscribers = allSubscriptions.filter(s => s && s.type !== 'free').length;

  // Previous period subscriber estimate (we don't have historical data, use 0 change)
  const subscribersChange = 0;

  // Views from GA4 (real data) or fallback to transaction count
  const ga4Views = pageViewData?.totalViews || 0;
  const ga4PrevViews = pageViewData?.previousTotalViews || 0;
  const totalViews = ga4Views > 0 ? ga4Views : current.length;
  const viewsChange = ga4Views > 0
    ? computeChange(ga4Views, ga4PrevViews)
    : computeChange(current.length, previous.length);

  // Conversion rate: transactions / views approximation
  const conversionRate = totalViews > 0 ? Math.round((current.length / totalViews) * 100) : 0;
  const prevViews = ga4PrevViews > 0 ? ga4PrevViews : previous.length;
  const prevConversion = prevViews > 0 ? Math.round((previous.length / prevViews) * 100) : 0;
  const conversionChange = computeChange(conversionRate, prevConversion);

  const revenueSources = computeRevenueSources(current, previous);
  const revenueDaily = computeDailyRevenue(transactions, rangeDays);
  const performanceByListing = groupByListing(current);

  // ---- Audience data ----
  const totalAudience = audienceLists.reduce((sum, list) => {
    const memberCount = list.memberCount || list.members?.length || 0;
    return sum + memberCount;
  }, 0);
  const audienceChange = 0; // No historical comparison available from API

  // Metrics we do not actually measure are reported as null so the UI can say
  // so, rather than as zeros or stand-ins that read like findings:
  //   - newSubscribers used to be the paid-subscriber count under a different
  //     name, which is a different number entirely.
  //   - growthRate was derived from that, so it inherited the error.
  //   - churn has no source at all; rendering it as 0% told creators nobody
  //     had left.
  //   - audienceDaily was a generated curve from ~80% of total up to total,
  //     drawn regardless of what actually happened. A chart is read as
  //     evidence, so inventing one is the worst of these.
  // Tracking these properly is the parked Insights analytics work.
  const newSubscribers = null;
  const newSubscribersChange = 0;
  const growthRate = null;
  const churnRate = null;
  const audienceDaily = [];

  // Top segments from audience lists
  const topSegments = audienceLists
    .map(list => ({
      id: list.id || list._id,
      name: list.name || 'Untitled List',
      subscribers: list.memberCount || list.members?.length || 0,
      // Per-segment growth and engagement have no source yet; null so the
      // table shows "—" rather than claiming every list grew by 0%.
      growth: null,
      engagement: null,
    }))
    .sort((a, b) => b.subscribers - a.subscribers)
    .slice(0, 10);

  // ---- Engagement data ----
  const isListingRelevant = (type) =>
    type === 'live-room' || type === config.listingTypes['live-room'] ||
    isPageType(type);

  const roomListings = listings.filter(
    l => isListingRelevant(l.attributes?.publicData?.type)
  );
  const roomTransactions = current.filter(t =>
    isListingRelevant(t.listing?.attributes?.publicData?.type || '')
  );
  const prevRoomTransactions = previous.filter(t =>
    isListingRelevant(t.listing?.attributes?.publicData?.type || '')
  );
  const roomParticipations = roomTransactions.length;
  const participationsChange = computeChange(roomTransactions.length, prevRoomTransactions.length);

  // Email open rate from sent emails
  let emailOpenRate = 0;
  let openRateChange = 0;
  if (emails && emails.length > 0) {
    const sentEmails = emails.filter(e => e.status === 'sent');
    if (sentEmails.length > 0) {
      const totalOpenRate = sentEmails.reduce((sum, e) => sum + (e.openRate || 0), 0);
      emailOpenRate = Math.round(totalOpenRate / sentEmails.length);
    }
  }

  const engagementDaily = computeEngagementDaily(transactions, rangeDays);

  // Build a lookup of GA4 views per listing ID from viewsByPath
  const viewsByListingId = {};
  if (pageViewData?.viewsByPath) {
    for (const [path, data] of Object.entries(pageViewData.viewsByPath)) {
      // Listing page paths follow /l/:slug/:id pattern
      const parts = path.split('/');
      const listingId = parts[parts.length - 1];
      if (listingId) {
        viewsByListingId[listingId] = (viewsByListingId[listingId] || 0) + data.views;
      }
    }
  }

  // Top content: combine listing performance with GA4 view counts
  const topContent = performanceByListing.slice(0, 10).map(item => ({
    id: item.id,
    name: item.name,
    type: isOrg ? 'Production' : 'Live Room',
    views: viewsByListingId[item.id] || item.transactions,
    engRate: item.transactions > 0 ? Math.min(100, Math.round((item.revenue / item.transactions))) : 0,
  }));

  // Content performance summary cards
  const contentPerformance = [];
  if (roomListings.length > 0) {
    contentPerformance.push({
      label: isOrg ? 'Productions' : 'Live Rooms',
      title: roomListings[0]?.attributes?.title || (isOrg ? 'Production' : 'Live Room'),
      stat: `${roomParticipations} participants`,
      change: participationsChange,
    });
  }
  if (emails && emails.length > 0) {
    const latestEmail = emails[0];
    contentPerformance.push({
      label: 'Newsletter',
      title: latestEmail.subject || 'Email Campaign',
      stat: `${emailOpenRate}% open rate`,
      change: openRateChange,
    });
  }

  // Poll data from SendBird
  const pollResponsesCount = pollData?.totalResponses || 0;
  const totalPolls = pollData?.totalPolls || 0;
  const topPolls = (pollData?.polls || []).slice(0, 5);

  // Add top poll to content performance if available
  if (topPolls.length > 0) {
    const topPoll = topPolls[0];
    contentPerformance.unshift({
      label: 'Polls',
      title: topPoll.question?.length > 25 ? topPoll.question.slice(0, 25) + '...' : topPoll.question,
      stat: `${pollResponsesCount} responses`,
      change: 0,
    });
  }

  // Add polls to top content table
  topPolls.forEach(poll => {
    topContent.push({
      id: poll.id,
      name: poll.question?.length > 30 ? poll.question.slice(0, 30) + '...' : poll.question,
      type: 'Poll',
      views: poll.totalVotes,
      engRate: poll.memberCount > 0 ? Math.round((poll.totalVotes / poll.memberCount) * 100) : 0,
    });
  });

  // Re-sort top content by views
  topContent.sort((a, b) => b.views - a.views);

  return {
    // Earning
    totalRevenue,
    revenueChange,
    paidSubscribers,
    subscribersChange,
    totalViews,
    viewsChange,
    conversionRate,
    conversionChange,
    revenueSources,
    revenueDaily,
    performanceByListing,
    // Audience
    totalAudience,
    audienceChange,
    newSubscribers,
    newSubscribersChange,
    growthRate,
    churnRate,
    audienceDaily,
    topSegments,
    audienceLists,
    // Engagement
    totalTransactions: current.length,
    transactionsChange: computeChange(current.length, previous.length),
    roomParticipations,
    participationsChange,
    emailOpenRate,
    openRateChange,
    pollResponsesCount,
    pollResponsesChange: 0,
    totalPolls,
    topPolls,
    engagementDaily,
    topContent,
    contentPerformance,
  };
};

// ================ Thunks ================ //

/**
 * Fetch transactions for Creator context (own transactions via SDK).
 */
const fetchCreatorTransactions = () => async (dispatch, getState, sdk) => {
  try {
    const response = await sdk.transactions.query({
      only: 'sale',
      include: ['listing'],
    });
    dispatch(addMarketplaceEntities(response));
    return denormalisedResponseEntities(response);
  } catch (e) {
    // Degrade to zero revenue/transactions rather than failing the whole
    // Insights page — every other source isolates its own failure this way.
    console.warn('Creator transactions fetch failed:', e?.message || e);
    return [];
  }
};

/**
 * Fetch the rented-insights bundle for an org. Returns the raw shapes
 * the duck already understands (transactions / listings / audienceLists)
 * plus an `assignments` summary for badging. Each row carries a
 * `provenance.kind === 'rented'` tag.
 *
 * Returns null when the call fails so loadData can degrade gracefully
 * to owned-only — the rented overlay is non-critical.
 */
const fetchRentedInsights = (orgId) => async () => {
  try {
    const response = await assignmentsAPI.getRentedInsights(orgId);
    return response?.data?.data || null;
  } catch (e) {
    console.warn('Rented insights fetch failed:', e?.message || e);
    return null;
  }
};

/**
 * Zip two daily series ([{ date, [valueKey]: number }]) into a single
 * `[{ date, owned, rented, [valueKey]: total }]` series for stacked
 * charts. The two inputs always share the same date labels by
 * construction (both processInsightsData passes use the same
 * rangeDays + bucket strategy), but we still zip by date label rather
 * than by index so monthly buckets that exist on only one side line up
 * correctly when stacked.
 */
const zipDailySeries = (ownedSeries, rentedSeries, valueKey) => {
  const owned = Array.isArray(ownedSeries) ? ownedSeries : [];
  const rented = Array.isArray(rentedSeries) ? rentedSeries : [];
  // Preserve owned's order for the page-level x-axis. Append rented
  // buckets that don't exist on the owned side at the end (only
  // possible when monthly buckets fall outside the owned tx set).
  const seen = new Set();
  const out = [];
  const rentedByDate = {};
  for (const p of rented) {
    if (p?.date) rentedByDate[p.date] = p[valueKey] || 0;
  }
  for (const p of owned) {
    const date = p?.date;
    if (!date) continue;
    seen.add(date);
    const ownedVal = p[valueKey] || 0;
    const rentedVal = rentedByDate[date] || 0;
    out.push({
      date,
      owned: ownedVal,
      rented: rentedVal,
      [valueKey]: ownedVal + rentedVal,
    });
  }
  for (const p of rented) {
    if (p?.date && !seen.has(p.date)) {
      const rentedVal = p[valueKey] || 0;
      out.push({
        date: p.date,
        owned: 0,
        rented: rentedVal,
        [valueKey]: rentedVal,
      });
    }
  }
  return out;
};

/**
 * Run processInsightsData on the rented inputs (when present), then
 * fold the rented totals back into the owned ones so the page-level
 * scalars become the *combined* (owned + rented) value. The original
 * owned slice is preserved under state.provenance so tab components
 * can show the breakdown when the ProvenanceFilter is flipped.
 *
 * Pure-add scalars (revenue, audience, paidSubs, transactions,
 * roomParticipations, poll responses) sum cleanly. Average-style
 * metrics (emailOpenRate, growthRate, churnRate, change deltas) and
 * cumulative-only metrics keep the owned value — rented emails aren't
 * surfaced today, and averaging % rates across owners is misleading
 * without a weighted base.
 */
const mergeRentedIntoInsights = (ownedData, rentedBundle, rangeDays) => {
  const empty = {
    totalRevenue: { owned: 0, rented: 0 },
    paidSubscribers: { owned: 0, rented: 0 },
    totalAudience: { owned: 0, rented: 0 },
    totalTransactions: { owned: 0, rented: 0 },
    roomParticipations: { owned: 0, rented: 0 },
    pollResponsesCount: { owned: 0, rented: 0 },
  };
  const assignments = rentedBundle?.assignments || [];

  if (!rentedBundle) {
    const provenance = {
      ...empty,
      totalRevenue: { owned: ownedData.totalRevenue, rented: 0 },
      paidSubscribers: { owned: ownedData.paidSubscribers, rented: 0 },
      totalAudience: { owned: ownedData.totalAudience, rented: 0 },
      totalTransactions: { owned: ownedData.totalTransactions, rented: 0 },
      roomParticipations: { owned: ownedData.roomParticipations, rented: 0 },
      pollResponsesCount: { owned: ownedData.pollResponsesCount, rented: 0 },
    };
    // Tag daily series so charts can read owned/rented uniformly even
    // when there's no rented overlay. rented stays 0 and the chart
    // falls back to its single-series rendering.
    const combinedNoRented = {
      ...ownedData,
      revenueDaily: zipDailySeries(ownedData.revenueDaily, [], 'revenue'),
      audienceDaily: zipDailySeries(ownedData.audienceDaily, [], 'audience'),
      engagementDaily: zipDailySeries(ownedData.engagementDaily, [], 'views'),
    };
    return { provenance, combined: combinedNoRented, rentedAssignments: [] };
  }

  const rentedTransactions = rentedBundle.transactions || [];
  const rentedListings = rentedBundle.listings || [];
  const rentedAudienceLists = rentedBundle.audienceLists || [];

  const rentedData = processInsightsData(
    rentedTransactions,
    rentedListings,
    rentedAudienceLists,
    /* emails */ [],
    rangeDays,
    /* pollData */ null,
    /* isOrg */ true,
    /* pageViewData */ null
  );

  const provenance = {
    totalRevenue: { owned: ownedData.totalRevenue, rented: rentedData.totalRevenue },
    paidSubscribers: { owned: ownedData.paidSubscribers, rented: rentedData.paidSubscribers },
    totalAudience: { owned: ownedData.totalAudience, rented: rentedData.totalAudience },
    totalTransactions: { owned: ownedData.totalTransactions, rented: rentedData.totalTransactions },
    roomParticipations: { owned: ownedData.roomParticipations, rented: rentedData.roomParticipations },
    pollResponsesCount: { owned: ownedData.pollResponsesCount, rented: 0 },
  };

  const combined = {
    ...ownedData,
    // Pure-add scalars switch to combined for the existing flat
    // selectors (which back the default `All` slice on every hero card).
    totalRevenue: ownedData.totalRevenue + rentedData.totalRevenue,
    paidSubscribers: ownedData.paidSubscribers + rentedData.paidSubscribers,
    totalAudience: ownedData.totalAudience + rentedData.totalAudience,
    totalTransactions: ownedData.totalTransactions + rentedData.totalTransactions,
    roomParticipations: ownedData.roomParticipations + rentedData.roomParticipations,
    // Daily series → `{ date, owned, rented, <valueKey>: total }` so
    // charts can render stacked Areas (filter='all') or a single
    // sliced Area (filter='owned' / 'rented') using one data array.
    revenueDaily: zipDailySeries(ownedData.revenueDaily, rentedData.revenueDaily, 'revenue'),
    audienceDaily: zipDailySeries(ownedData.audienceDaily, rentedData.audienceDaily, 'audience'),
    engagementDaily: zipDailySeries(ownedData.engagementDaily, rentedData.engagementDaily, 'views'),
    // Surface rented audience lists alongside the owned ones so the
    // Audience-tab "Top segments" stays honest about where members
    // come from.
    audienceLists: [
      ...(ownedData.audienceLists || []),
      ...rentedAudienceLists,
    ],
  };

  return { provenance, combined, rentedAssignments: assignments };
};

/**
 * Fetch transactions for Org context (via integration SDK server endpoint).
 */
const fetchOrgTransactions = (orgId) => async (dispatch) => {
  try {
    const response = await transactionsAPI.query({
      meta_organizationId: orgId,
      include: ['listing'],
    });
    dispatch(addMarketplaceEntities(response.data));
    return denormalisedResponseEntities(response.data);
  } catch (e) {
    // Degrade to zero revenue/transactions rather than failing the whole
    // Insights page (the org endpoint depends on Integration SDK creds).
    console.warn('Org transactions fetch failed:', e?.message || e);
    return [];
  }
};

/**
 * Fetch own listings (Creator context).
 */
const fetchCreatorListings = () => async (dispatch, getState, sdk) => {
  const response = await sdk.ownListings.query({
    pub_type: config.listingTypes['live-room'],
    states: ['published'],
    include: ['images'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    'limit.images': 1,
  });
  dispatch(addMarketplaceEntities(response));
  return response.data.data || [];
};

/**
 * Fetch org listings (show pages filtered by orgId).
 */
const fetchOrgListings = (orgId) => async (dispatch, getState, sdk) => {
  const response = await sdk.listings.query({
    pub_type: PAGE_TYPE_QUERY,
    pub_organizationId: orgId,
  });
  dispatch(addMarketplaceEntities(response));
  return response.data.data || [];
};

/**
 * Fetch audience lists. Org-scoped when an orgId is present (otherwise a
 * non-owner viewing the org saw their own/empty audience, not the org's).
 */
const fetchAudienceLists = orgId => async () => {
  try {
    const response = orgId
      ? await orgAudienceAPI.getLists(orgId)
      : await audienceAPI.getLists();
    return response.data?.data?.lists || [];
  } catch (e) {
    console.error('Error fetching audience lists:', e);
    return [];
  }
};

/**
 * Call-to-action clicks for the range (server/api/cta/clicks.js), with email
 * campaigns named by their subject. Never fails the Insights load.
 */
const fetchCtaClicks = (orgId, rangeDays, emails = []) => async dispatch => {
  try {
    const response = await ctaAPI.clicks(orgId, rangeDays);
    const body = response?.data ?? response;
    const data = body?.data || null;
    if (data) {
      data.items = (data.items || []).map(item => ({
        ...item,
        name:
          item.source === 'email'
            ? (emails || []).find(e => String(e.id) === String(item.id))?.subject || null
            : null,
      }));
    }
    dispatch({ type: SET_CTA_CLICKS, payload: data });
  } catch (e) {
    dispatch({ type: SET_CTA_CLICKS, payload: null });
  }
};

/**
 * Fetch sent email campaigns for open rate data. Org-scoped when orgId set.
 */
const fetchEmailCampaigns = orgId => async () => {
  try {
    const response = orgId
      ? await orgAudienceAPI.getEmails(orgId)
      : await audienceAPI.getEmails();
    return response.data?.data?.emails || [];
  } catch (e) {
    console.error('Error fetching email campaigns:', e);
    return [];
  }
};

/**
 * Fetch poll data from SendBird channels.
 * Scans all accessible channels, loads recent messages, and extracts poll stats.
 */
const fetchPollData = (orgId) => async () => {
  try {
    // Chat is no longer connected on every page, so Insights connects for
    // the poll results when it needs them.
    let sb = getSendbirdInstance();
    if (!sb) {
      const { userId, accessToken } = await sendbirdAPI.getToken();
      sb = await initializeSendbird(userId, accessToken);
    }
    if (!sb) {
      return { polls: [], totalResponses: 0, totalPolls: 0 };
    }

    // Query channels - filter by org context if applicable
    const listQuery = sb.groupChannel.createMyGroupChannelListQuery({
      includeEmpty: true,
      limit: 100,
    });

    const channels = await listQuery.next();

    // Filter channels by context (org channels have orgId in data)
    const relevantChannels = orgId
      ? channels.filter(ch => {
          try {
            const data = ch.data ? JSON.parse(ch.data) : {};
            return data.orgId === orgId;
          } catch { return false; }
        })
      : channels.filter(ch => {
          try {
            const data = ch.data ? JSON.parse(ch.data) : {};
            return !data.orgId;
          } catch { return true; }
        });

    const allPolls = [];

    // Scan each channel for poll messages (limit to last 200 messages per channel)
    for (const channel of relevantChannels.slice(0, 20)) {
      try {
        const params = {
          prevResultSize: 200,
          nextResultSize: 0,
          isInclusive: true,
          reverse: false,
        };
        const messages = await channel.getMessagesByTimestamp(Date.now(), params);

        for (const msg of messages) {
          if (!msg.data) continue;
          try {
            const parsed = JSON.parse(msg.data);
            if (parsed?.poll) {
              const poll = parsed.poll;
              const votes = poll.votes || {};
              const totalVotes = Object.values(votes).reduce(
                (sum, voters) => sum + (Array.isArray(voters) ? voters.length : 0), 0
              );
              allPolls.push({
                id: poll.id,
                question: poll.question,
                options: poll.options || [],
                totalVotes,
                optionVotes: Object.entries(votes).map(([idx, voters]) => ({
                  index: Number(idx),
                  option: (poll.options || [])[Number(idx)] || `Option ${Number(idx) + 1}`,
                  votes: Array.isArray(voters) ? voters.length : 0,
                })),
                createdAt: poll.createdAt || msg.createdAt,
                channelUrl: channel.url,
                channelName: channel.name,
                memberCount: channel.memberCount || 0,
              });
            }
          } catch { /* skip unparseable messages */ }
        }
      } catch (e) {
        console.warn(`Error scanning channel ${channel.url} for polls:`, e.message);
      }
    }

    const totalResponses = allPolls.reduce((sum, p) => sum + p.totalVotes, 0);

    return {
      polls: allPolls.sort((a, b) => b.totalVotes - a.totalVotes),
      totalResponses,
      totalPolls: allPolls.length,
    };
  } catch (e) {
    console.warn('Error fetching poll data from SendBird:', e.message);
    return { polls: [], totalResponses: 0, totalPolls: 0 };
  }
};

/**
 * Fetch page view data from GA4 via our analytics API.
 * Returns { totalViews, previousTotalViews, viewsByPath, viewsByDate }
 */
const fetchPageViews = (rangeDays, listingIds = []) => async () => {
  try {
    const endDate = 'today';
    const startDate = `${rangeDays}daysAgo`;
    // Filter to listing pages (/l/ prefix)
    const response = await analyticsAPI.getPageViews({
      startDate,
      endDate,
      pathPrefix: '/l/',
    });
    return response.data?.data || { totalViews: 0, previousTotalViews: 0, viewsByPath: {}, viewsByDate: {} };
  } catch (e) {
    console.warn('Error fetching page views from GA4:', e.message);
    return { totalViews: 0, previousTotalViews: 0, viewsByPath: {}, viewsByDate: {} };
  }
};

/**
 * Invitation totals for manual calendar events (the "RSVPs logged in
 * Insights" step agreed on 1 Sep 2026). Records live beside the events: on
 * the creator's own profile, or behind the org calendar endpoint.
 */
const fetchCalendarInviteTotals = orgId => async (dispatch, getState) => {
  try {
    if (orgId) {
      const res = await orgCalendarAPI.listEvents(orgId);
      return summariseInvites(res?.data?.data?.invites || {});
    }
    const { currentUser } = getState().user;
    return summariseInvites(currentUser?.attributes?.profile?.privateData?.calendarInvites || {});
  } catch (e) {
    console.warn('Calendar invite totals unavailable:', e?.message || e);
    return summariseInvites({});
  }
};

/**
 * Main loadData function called by route configuration.
 * Fetches all data and processes it into the state shape.
 */
export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const orgId = params?.orgId;
  dispatch(setInitialState(orgId));
  dispatch(fetchInsightsRequest());

  const state = getState();
  const rangeDays = state.InsightsPage.dateRange || 30;

  try {
    // Fetch org data if orgId is present
    if (orgId) {
      await dispatch(fetchOrganizationById(orgId));
    }

    // Fetch all data in parallel. Rented overlay is org-only.
    const [transactions, listings, audienceLists, emails, pollData, pageViewData, rentedBundle, calendarInvites] = await Promise.all([
      orgId
        ? dispatch(fetchOrgTransactions(orgId))
        : dispatch(fetchCreatorTransactions()),
      orgId
        ? dispatch(fetchOrgListings(orgId))
        : dispatch(fetchCreatorListings()),
      dispatch(fetchAudienceLists(orgId)),
      dispatch(fetchEmailCampaigns(orgId)),
      dispatch(fetchPollData(orgId)),
      dispatch(fetchPageViews(rangeDays)),
      orgId ? dispatch(fetchRentedInsights(orgId)) : Promise.resolve(null),
      dispatch(fetchCalendarInviteTotals(orgId)),
    ]);

    dispatch(fetchCtaClicks(orgId, rangeDays, emails));

    // Process owned-only data first — preserves prior behavior on the
    // creator side and on orgs with no rented overlay.
    const ownedData = processInsightsData(transactions, listings, audienceLists, emails, rangeDays, pollData, !!orgId, pageViewData);

    // Compute the provenance split (and the combined flat numbers used
    // by the existing selectors/UI) from the rented bundle.
    const { provenance, combined, rentedAssignments } = mergeRentedIntoInsights(
      ownedData,
      rentedBundle,
      rangeDays
    );

    dispatch(fetchInsightsSuccess({
      ...combined,
      provenance,
      rentedAssignments,
      calendarInvites,
      dateRange: rangeDays,
      // Keep raw owned inputs so a date-range change can re-process
      // everything from cache without re-fetching. These must stay the
      // *owned-only* raw shapes — never the merged combined output —
      // or the rented overlay gets double-counted on re-process.
      _rawTransactions: transactions,
      _rawListings: listings,
      _rawAudienceLists: audienceLists,
      _rawEmails: emails,
      _rawPollData: pollData,
      _rawPageViewData: pageViewData,
      _rawRentedBundle: rentedBundle,
      _isOrg: !!orgId,
    }));

    return combined;
  } catch (e) {
    console.error('Error loading insights data:', e);
    dispatch(fetchInsightsError(storableError(e)));
    return null;
  }
};

/**
 * Re-process insights when the date range changes.
 *
 * Transactions/audience/emails/polls are fetched unbounded, so they are
 * re-sliced from the cached raw inputs with no network call. Page views
 * are the exception: GA4 returns a window-aggregated total, so they must
 * be re-fetched for the new range or Views/Conversion would stay frozen
 * at the previous window. We therefore re-process twice: once instantly
 * from cache (snappy graph update), then again once fresh page views
 * arrive (corrects Views/Conversion).
 */
export const changeDateRange = (rangeDays) => async (dispatch, getState) => {
  dispatch(setDateRangeAction(rangeDays));
  dispatch(
    fetchCtaClicks(getState().InsightsPage.orgId || null, rangeDays, getState().InsightsPage._rawEmails || [])
  );

  // Only re-process after an initial load has populated the raw cache.
  // (Guarding on transactions would wrongly freeze the filter for orgs
  // that have views/audience but no sales yet.)
  if (!getState().InsightsPage.insightsLoaded) {
    return;
  }

  // Re-process the cached owned-only raw inputs against `pageViewData`,
  // merge the rented overlay once, and persist the full cache so repeated
  // range changes stay correct.
  const reprocess = pageViewData => {
    const {
      _rawTransactions,
      _rawListings,
      _rawAudienceLists,
      _rawEmails,
      _rawPollData,
      _rawRentedBundle,
      _isOrg,
    } = getState().InsightsPage;

    const ownedData = processInsightsData(
      _rawTransactions || [],
      _rawListings || [],
      _rawAudienceLists || [],
      _rawEmails || [],
      rangeDays,
      _rawPollData,
      _isOrg,
      pageViewData
    );
    const { provenance, combined, rentedAssignments } = mergeRentedIntoInsights(
      ownedData,
      _rawRentedBundle,
      rangeDays
    );

    dispatch(fetchInsightsSuccess({
      ...combined,
      provenance,
      rentedAssignments,
      dateRange: rangeDays,
      _rawTransactions,
      _rawListings,
      _rawAudienceLists,
      _rawEmails,
      _rawPollData,
      _rawPageViewData: pageViewData,
      _rawRentedBundle,
      _isOrg,
    }));
  };

  // 1) Instant pass with the page views we already have — revenue,
  //    audience, emails and polls update immediately for the new window.
  reprocess(getState().InsightsPage._rawPageViewData);

  // 2) Re-fetch window-aggregated page views and re-process to correct
  //    Views/Conversion. Guard against a stale response overwriting a
  //    newer selection if the user clicks ranges quickly.
  try {
    const pageViewData = await dispatch(fetchPageViews(rangeDays));
    if (getState().InsightsPage.dateRange === rangeDays) {
      reprocess(pageViewData);
    }
  } catch (e) {
    // Keep the instant result; Views stay at the prior window's number.
    console.warn('Page views refetch on range change failed:', e?.message || e);
  }
};
